import { useState, useEffect } from "react";
import axios from "axios";
import { Layout } from "@/components/Layout";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Edit, Trash2, FolderOpen, FileText, Upload } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminMaterials = () => {
  const { t } = useLanguage();
  
  // Categories state
  const [categories, setCategories] = useState([]);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: "", description: "", order: 0 });
  
  // Materials state
  const [materials, setMaterials] = useState([]);
  const [materialDialogOpen, setMaterialDialogOpen] = useState(false);
  const [materialForm, setMaterialForm] = useState({
    title: "",
    description: "",
    category_id: "",
    file_data: "",
    file_name: ""
  });
  
  const [loading, setLoading] = useState(true);
  const [deleteItem, setDeleteItem] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("aestha_token");
      const [catRes, matRes] = await Promise.all([
        axios.get(`${API}/admin/study-categories`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/study-materials`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setCategories(catRes.data);
      setMaterials(matRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  // Category functions
  const openCategoryDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        name: category.name,
        description: category.description || "",
        order: category.order || 0
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({ name: "", description: "", order: categories.length });
    }
    setCategoryDialogOpen(true);
  };

  const saveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.error("Nome da categoria é obrigatório");
      return;
    }

    try {
      const token = localStorage.getItem("aestha_token");
      if (editingCategory) {
        await axios.put(`${API}/admin/study-categories/${editingCategory.id}`, categoryForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Categoria atualizada!");
      } else {
        await axios.post(`${API}/admin/study-categories`, categoryForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Categoria criada!");
      }
      setCategoryDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Erro ao salvar categoria");
    }
  };

  // Material functions
  const openMaterialDialog = () => {
    setMaterialForm({
      title: "",
      description: "",
      category_id: categories[0]?.id || "",
      file_data: "",
      file_name: ""
    });
    setMaterialDialogOpen(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      toast.error("Apenas arquivos PDF são permitidos");
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      toast.error("Arquivo muito grande. Máximo 10MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setMaterialForm({
        ...materialForm,
        file_data: reader.result,
        file_name: file.name
      });
    };
    reader.readAsDataURL(file);
  };

  const saveMaterial = async () => {
    if (!materialForm.title.trim()) {
      toast.error("Título é obrigatório");
      return;
    }
    if (!materialForm.category_id) {
      toast.error("Selecione uma categoria");
      return;
    }
    if (!materialForm.file_data) {
      toast.error("Selecione um arquivo PDF");
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem("aestha_token");
      await axios.post(`${API}/admin/study-materials`, materialForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Material adicionado!");
      setMaterialDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Erro ao salvar material");
    } finally {
      setUploading(false);
    }
  };

  // Delete functions
  const handleDelete = async () => {
    if (!deleteItem) return;

    try {
      const token = localStorage.getItem("aestha_token");
      const endpoint = deleteItem.type === "category" 
        ? `${API}/admin/study-categories/${deleteItem.id}`
        : `${API}/admin/study-materials/${deleteItem.id}`;
      
      await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Excluído com sucesso!");
      setDeleteItem(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir");
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  return (
    <Layout>
      <div className="p-6 md:p-12" data-testid="admin-materials-page">
        {/* Header */}
        <div className="mb-8">
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">
            {t("administration")}
          </p>
          <h1 className="font-serif text-3xl md:text-4xl">{t("study_materials")}</h1>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="materials" className="w-full">
          <TabsList className="w-full grid grid-cols-2 mb-6">
            <TabsTrigger value="materials" className="font-mono uppercase text-xs tracking-widest" data-testid="materials-tab">
              {t("materials")}
            </TabsTrigger>
            <TabsTrigger value="categories" className="font-mono uppercase text-xs tracking-widest" data-testid="categories-tab">
              {t("categories")}
            </TabsTrigger>
          </TabsList>

          {/* Materials Tab */}
          <TabsContent value="materials">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-xl">{t("materials")}</h2>
              <Button 
                className="btn-primary" 
                onClick={openMaterialDialog}
                disabled={categories.length === 0}
                data-testid="new-material-btn"
              >
                <Plus size={16} className="mr-2" />
                {t("new_material")}
              </Button>
            </div>

            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-24 loading-skeleton" />
                ))}
              </div>
            ) : materials.length > 0 ? (
              <div className="space-y-4">
                {materials.map((material) => (
                  <div 
                    key={material.id} 
                    className="bento-item flex items-center justify-between"
                    data-testid={`material-${material.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <FileText size={20} strokeWidth={1} />
                      <div>
                        <h4 className="font-serif text-lg">{material.title}</h4>
                        <p className="text-xs font-mono text-muted-foreground">
                          {material.category_name} • {material.file_name} • {formatFileSize(material.file_size)}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeleteItem({ ...material, type: "material" })}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state border py-12">
                <FileText className="empty-state-icon" />
                <p className="empty-state-title">{t("no_materials")}</p>
                <p className="empty-state-description">
                  {categories.length === 0 ? t("create_first_category") : t("no_materials_in_category")}
                </p>
              </div>
            )}

            {/* Material Dialog */}
            <Dialog open={materialDialogOpen} onOpenChange={setMaterialDialogOpen}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-serif text-xl">
                    {t("new_material")}
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  <div>
                    <Label className="label-mono">{t("material_title")} *</Label>
                    <Input
                      value={materialForm.title}
                      onChange={(e) => setMaterialForm({ ...materialForm, title: e.target.value })}
                      className="input-paper"
                      placeholder="Ex: Apostila de Anatomia Facial"
                    />
                  </div>

                  <div>
                    <Label className="label-mono">{t("material_description")}</Label>
                    <Textarea
                      value={materialForm.description}
                      onChange={(e) => setMaterialForm({ ...materialForm, description: e.target.value })}
                      placeholder="Descrição opcional..."
                      rows={3}
                    />
                  </div>

                  <div>
                    <Label className="label-mono">{t("select_category")} *</Label>
                    <Select
                      value={materialForm.category_id}
                      onValueChange={(value) => setMaterialForm({ ...materialForm, category_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t("select_category")} />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="label-mono">{t("select_pdf")} *</Label>
                    <div className="border-2 border-dashed p-6 text-center">
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={handleFileSelect}
                        className="hidden"
                        id="pdf-upload"
                      />
                      <label htmlFor="pdf-upload" className="cursor-pointer">
                        {materialForm.file_name ? (
                          <div className="flex items-center justify-center gap-2 text-sm">
                            <FileText size={20} />
                            <span>{materialForm.file_name}</span>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <Upload size={32} className="mx-auto text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">
                              Clique para selecionar um PDF (máx. 10MB)
                            </p>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>

                  <Button 
                    onClick={saveMaterial} 
                    className="btn-primary w-full"
                    disabled={uploading}
                  >
                    {uploading ? "Enviando..." : t("upload_pdf")}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-xl">{t("categories")}</h2>
              <Button 
                className="btn-primary" 
                onClick={() => openCategoryDialog()}
                data-testid="new-category-btn"
              >
                <Plus size={16} className="mr-2" />
                {t("new_category")}
              </Button>
            </div>

            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-20 loading-skeleton" />
                ))}
              </div>
            ) : categories.length > 0 ? (
              <div className="space-y-4">
                {categories.map((category) => (
                  <div 
                    key={category.id} 
                    className="bento-item flex items-center justify-between"
                    data-testid={`category-${category.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <FolderOpen size={20} strokeWidth={1} />
                      <div>
                        <h4 className="font-serif text-lg">{category.name}</h4>
                        {category.description && (
                          <p className="text-xs text-muted-foreground">{category.description}</p>
                        )}
                        <p className="text-xs font-mono text-muted-foreground">
                          {materials.filter(m => m.category_id === category.id).length} {t("materials")}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openCategoryDialog(category)}
                      >
                        <Edit size={14} />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteItem({ ...category, type: "category" })}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state border py-12">
                <FolderOpen className="empty-state-icon" />
                <p className="empty-state-title">{t("no_categories")}</p>
              </div>
            )}

            {/* Category Dialog */}
            <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-serif text-xl">
                    {editingCategory ? t("edit") : t("new_category")}
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  <div>
                    <Label className="label-mono">{t("category_name")} *</Label>
                    <Input
                      value={categoryForm.name}
                      onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                      className="input-paper"
                      placeholder="Ex: Anatomia"
                    />
                  </div>

                  <div>
                    <Label className="label-mono">{t("category_description")}</Label>
                    <Textarea
                      value={categoryForm.description}
                      onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                      placeholder="Descrição opcional..."
                      rows={3}
                    />
                  </div>

                  <Button 
                    onClick={saveCategory} 
                    className="btn-primary w-full"
                  >
                    {editingCategory ? t("save_changes") : t("create")}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>
        </Tabs>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteItem} onOpenChange={() => setDeleteItem(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-serif text-xl">
                {t("delete")}?
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("delete_template_warning")} "{deleteItem?.name || deleteItem?.title}" {t("will_be_deleted")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="btn-secondary">{t("cancel")}</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {t("delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default AdminMaterials;
